import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import AIThinking from "@/components/AIThinking";
import Navbar from "@/components/Navbar";
import Logo from "@/components/Logo";
import Disclaimer from "@/components/Disclaimer";
import RichText from "@/components/RichText";
import NswAgentCheck from "@/components/NswAgentCheck";
import { STATES } from "@/lib/states";
import { DEMO_QA } from "@/data/demoQA";
import { Brain, Send, Loader2, ChevronRight, ShieldCheck, Save, MapPin, BookOpen, Sparkles, Share2, Copy, Check, Lock } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";

const STORAGE_KEY = "tt_brain_session";

function getOrCreateSessionId() {
 let id = localStorage.getItem(STORAGE_KEY);
 if (!id) {
 id = (crypto?.randomUUID?.() || (Date.now() + "-" + Math.random().toString(16).slice(2)));
 localStorage.setItem(STORAGE_KEY, id);
 }
 return id;
}

const EXAMPLES_BY_STATE = {
 QLD: [
 "My landlord won't return my bond after I moved out, what should I do?",
 "Can my agent inspect the place every month in QLD?",
 "The roof has been leaking for three weeks and the agent hasn't fixed it.",
 ],
 NSW: [
 "My landlord wants to raise the rent by 15%. Can I challenge that?",
 "Can the landlord end my fixed-term lease without giving a reason in NSW?",
 "The agent let themselves in without notice, what are my rights?",
 ],
 VIC: [
 "Can my rental provider refuse my emotional support dog?",
 "There's mould on the bedroom wall, is it my problem or theirs?",
 "What changed in November 2025 about no-reason notices?",
 ],
};

export default function BrainPage() {
 const navigate = useNavigate();
 const token = typeof window !== "undefined" ? localStorage.getItem("tt_token") : null;
 const isAuthed = !!token;

 const [state, setState] = useState(localStorage.getItem("tt_state") || "QLD");
 const [question, setQuestion] = useState("");
 const [loading, setLoading] = useState(false);
 const [conversation, setConversation] = useState([]); // [{role, content, qa_id?, truncated?, requires_signup?}]
 const [saveOpen, setSaveOpen] = useState(false);
 const [saveQaId, setSaveQaId] = useState(null);
 const [saveAddress, setSaveAddress] = useState("");
 const [saving, setSaving] = useState(false);

 useEffect(() => { localStorage.setItem("tt_state", state); }, [state]);

 const ask = async (text) => {
 const q = (text || question).trim();
 if (!q || loading) return;
 setLoading(true);
 setConversation(cs => [...cs, { role: "user", content: q }]);
 setQuestion("");
 try {
 const { data } = await axios.post(`${API}/brain/ask`, {
 question: q,
 state,
 session_id: getOrCreateSessionId(),
 }, {
 headers: isAuthed ? { Authorization: `Bearer ${token}` } : {},
 });
 setConversation(cs => [...cs, {
 role: "assistant",
 content: data.answer,
 qa_id: data.id,
 truncated: data.truncated,
 requires_signup: data.requires_signup,
 }]);
 } catch (e) {
 // Backend returns descriptive `detail` messages for the real blocking
 // cases (429 rate limit, 402 quota exceeded, 503 AI budget exhausted).
 // Show those verbatim. Fall back to the friendly generic line only for
 // true server errors or network blips.
 const status = e?.response?.status;
 const detail = e?.response?.data?.detail;
 let content;
 if (status === 429) {
 content = detail || "You've hit the anonymous question limit for your network. Sign up free to keep asking with your own quota.";
 } else if (status === 402) {
 content = detail || "You've used your AI questions for this billing period. Upgrade your plan to keep asking.";
 } else if (status === 503) {
 content = detail || "The Brain is taking a quick rest, our AI quota for today is full. Please try again in a few hours.";
 } else if (status === 400) {
 content = detail || "That question is too long or empty. Try rewording it in one or two sentences.";
 } else {
 content = "The Brain is having a moment, please try again in a sec.";
 }
 setConversation(cs => [...cs, {
 role: "assistant",
 content,
 error: true,
 requires_signup: status === 429,
 }]);
 } finally { setLoading(false); }
 };

 const openSave = (qaId) => {
 if (!isAuthed) { navigate("/register?next=/ask"); return; }
 setSaveQaId(qaId);
 setSaveOpen(true);
 };

 const saveToCase = async () => {
 if (!saveQaId) return;
 setSaving(true);
 try {
 const { data } = await axios.post(`${API}/brain/save`, { qa_id: saveQaId, address: saveAddress || undefined }, {
 headers: isAuthed ? { Authorization: `Bearer ${token}` } : {},
 });
 setSaveOpen(false);
 navigate(`/cases/${data.case_id}`);
 } catch (e) {
 alert(e.response?.data?.detail || "Couldn't save");
 } finally { setSaving(false); }
 };

 const examples = EXAMPLES_BY_STATE[state] || EXAMPLES_BY_STATE.QLD;

 // Curated showpiece answers for signed-out visitors. Admin-published
 // entries merge with the static seed, state-matched first. Zero AI cost.
 const [publishedQAs, setPublishedQAs] = useState([]);
 useEffect(() => {
 if (isAuthed) return;
 axios.get(`${API}/qa/list`).then(({ data }) => setPublishedQAs(data.items || [])).catch(() => {});
 }, [isAuthed]);
 const samples = React.useMemo(() => {
 const seen = new Set();
 const uniq = [...publishedQAs, ...DEMO_QA].filter(q => q.slug && !seen.has(q.slug) && seen.add(q.slug));
 const match = uniq.filter(q => q.state === state);
 const rest = uniq.filter(q => q.state !== state);
 return [...match, ...rest].slice(0, 3);
 }, [state, publishedQAs]);

 return (
 <div className="min-h-screen paper-grain">
 <Navbar />
 <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10">
 {/* Header */}
 <div className="text-center mb-10">
 <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E6F5F2] border border-[#1B6C62]/30 mb-4">
 <Brain size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="font-mono text-[10px] tracking-wider uppercase text-[#0D2B52]">The Tenant Brain · free</span>
 </div>
 <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] leading-[1.05] mb-3">
 Protection. Evidence. Accountability.<br />
 <span className="text-[#1B6C62]">The backup every Aussie renter deserves.</span>
 </h1>
 <p className="text-[#6B7280] max-w-xl mx-auto leading-relaxed">
 Built for Australian renters, trained on every state&apos;s Residential Tenancies Act, federal consumer law plus decades of tribunal decisions. Honest, evidence-backed answers. <strong>Not legal advice.</strong>
 </p>
 </div>

 {/* Conversation */}
 {conversation.length > 0 && (
 <div className="space-y-4 mb-6" data-testid="brain-conversation">
 {conversation.map((m, i) => (
 <div key={i} className={m.role === "user" ? "" : "card"}>
 {m.role === "user" ? (
 <div className="flex justify-end">
 <div className="bg-[#0D2B52] text-white px-4 py-2.5 max-w-xl text-sm font-serif">
 {m.content}
 </div>
 </div>
 ) : (
 <div data-testid={`brain-answer-${i}`}>
 <div className="flex items-start gap-2.5 mb-2 flex-wrap">
 <Brain size={16} strokeWidth={1.75} className="text-[#1B6C62] mt-0.5 flex-shrink-0" />
 <span className="label-mono">The Brain</span>
 <span className="text-[10px] text-[#6B7280] font-mono">· {state}</span>
 <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 bg-[#E6F5F2] text-[#0D2B52] font-mono text-[10px] tracking-wider uppercase border border-[#1B6C62]/30" data-testid={`brain-quick-overview-${i}`}>
 <Sparkles size={10} strokeWidth={1.75} className="text-[#1B6C62]" /> Quick overview
 </span>
 </div>
 <div className="prose prose-sm max-w-none font-serif text-[15px] leading-relaxed text-[#1A1A1A] whitespace-pre-wrap">
 <RichText text={m.content} state={state} />
 </div>
 {!m.requires_signup && (
 <div className="mt-4 p-4 bg-[#FAF7F0] border-l-2 border-[#1B6C62] flex flex-wrap items-start justify-between gap-3" data-testid={`brain-deep-upgrade-${i}`}>
 <div className="flex items-start gap-2 max-w-xl">
 <Sparkles size={14} strokeWidth={1.75} className="text-[#1B6C62] mt-1 flex-shrink-0" />
 <div className="text-sm text-[#0D2B52] font-serif leading-snug">
 <strong>This is the quick overview.</strong> For a full step-by-step tailored to your situation, with your documents read and your letters and forms drafted for you, upgrade to Deep AI.
 </div>
 </div>
 <Link to="/pricing" className="btn-primary text-xs whitespace-nowrap" data-testid={`brain-deep-upgrade-cta-${i}`}>See Deep AI plans</Link>
 </div>
 )}
 {m.truncated && m.requires_signup && (
 <div className="mt-4 p-4 bg-[#FAF7F0] border border-[#D4A24C]/40 flex flex-wrap items-center justify-between gap-3">
 <div className="flex items-center gap-2">
 <ShieldCheck size={16} strokeWidth={1.75} className="text-[#8A6D3B]" />
 <span className="text-sm text-[#0D2B52] font-serif">Sign up free to keep asking. Full answers, citations, plus save Q&As to a case.</span>
 </div>
 <Link to="/register?next=/ask" className="btn-primary text-xs" data-testid="brain-signup-cta">Start free</Link>
 </div>
 )}
 {m.truncated && !m.requires_signup && (
 <div className="mt-4 p-3 bg-[#FAF9F6] border border-[#E5E4DE] flex items-center justify-between gap-3 flex-wrap">
 <div className="flex items-center gap-2">
 <span className="text-xs text-[#6B7280] italic">Preview cut short. Sign up free to read the full answer.</span>
 </div>
 <Link to="/register?next=/ask" className="btn-primary text-xs" data-testid="brain-signup-cta">Start free</Link>
 </div>
 )}
 {m.qa_id && !m.requires_signup && (
 <div className="mt-3 flex items-center gap-3 flex-wrap">
 <button onClick={() => openSave(m.qa_id)} className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid={`brain-save-${m.qa_id}`}>
 <Save size={12} strokeWidth={1.5} /> Save this Q&A to a case
 </button>
 <ShareButton qaId={m.qa_id} />
 </div>
 )}
 {!m.requires_signup && (
 <div className="mt-3 pt-3 border-t border-[#E5E4DE] text-[12px] text-[#6B7280] font-serif italic" data-testid={`brain-related-${i}`}>
 Related reading: <a href="/api/agent-tricks" className="text-[#1B6C62] hover:underline not-italic font-normal">12 rental clauses your agent hopes you don&apos;t read &rarr;</a>
 </div>
 )}
 </div>
 )}
 </div>
 ))}
 {loading && (
 <AIThinking
 phrases={["Reading your state's Act", "Cross-referencing your question", "Drafting a clear answer", "Citing the relevant sections"]}
 testid="brain-loading"
 />
 )}
 </div>
 )}

 {/* Input — live ask box for signed-in users, signup CTA for visitors */}
 {isAuthed ? (
 <div className="card" data-testid="brain-input-card">
 <div className="flex items-center gap-2 mb-3">
 <MapPin size={13} strokeWidth={1.5} className="text-[#1B6C62]" />
 <span className="label-mono">Your state</span>
 <div className="flex gap-1 flex-wrap">
 {STATES.map(s => (
 <button key={s.code} onClick={() => setState(s.code)}
 className={`px-2 py-1 text-[11px] font-mono transition-colors ${state === s.code ? "bg-[#0D2B52] text-white" : "bg-white text-[#6B7280] border border-[#E5E4DE] hover:border-[#0D2B52]"}`}
 data-testid={`brain-state-${s.code}`}>
 {s.code}
 </button>
 ))}
 </div>
 </div>

 <textarea
 className="input-field text-base resize-none"
 rows={3}
 placeholder="Ask anything, bond disputes, mould, rent increases, no-grounds notices, your rights at exit…"
 value={question}
 onChange={e => setQuestion(e.target.value)}
 onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(); }}
 data-testid="brain-input"
 />
 <div className="flex justify-between items-center mt-3 flex-wrap gap-2">
 <span className="text-[11px] text-[#6B7280] italic">
 Signed in. Full Q&A access.
 </span>
 <button onClick={() => ask()} disabled={loading || !question.trim()}
 className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="brain-ask">
 <Send size={14} strokeWidth={1.5} /> {loading ? "Thinking…" : "Ask the Brain"}
 </button>
 </div>
 </div>
 ) : (
 <div className="card border-l-4 border-l-[#1B6C62]" data-testid="brain-signup-card">
 <div className="flex items-start gap-3">
 <div className="w-9 h-9 flex items-center justify-center bg-[#E6F5F2] border border-[#1B6C62]/30 flex-shrink-0">
 <Lock size={15} strokeWidth={1.75} className="text-[#1B6C62]" />
 </div>
 <div className="flex-1">
 <h3 className="font-serif text-xl text-[#0D2B52] mb-1">Want to ask your own question?</h3>
 <p className="text-sm text-[#1A1A1A] font-serif leading-relaxed mb-3">
 Create a free account and you get <strong>5 AI questions a month on us</strong>: full answers with Act
 citations, tribunal signposting and the ability to save any Q&amp;A straight into a case file. No card needed.
 </p>
 <div className="flex items-center gap-3 flex-wrap">
 <Link to="/register?next=/ask" className="btn-primary text-sm" data-testid="brain-locked-signup-cta">Start free</Link>
 <Link to="/login?next=/ask" className="text-sm text-[#1B6C62] hover:underline" data-testid="brain-locked-login-link">Already have an account? Sign in</Link>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Example questions (signed-in, when no conversation yet) */}
 {isAuthed && conversation.length === 0 && (
 <div className="mt-8" data-testid="brain-examples">
 <div className="label-mono mb-3 flex items-center gap-2">
 <Sparkles size={12} strokeWidth={1.5} className="text-[#1B6C62]" />
 <span>Try one of these</span>
 </div>
 <div className="space-y-2">
 {examples.map((q, i) => (
 <button key={i} onClick={() => ask(q)}
 className="w-full text-left bg-white border border-[#E5E4DE] hover:border-[#1B6C62] p-3 font-serif text-sm text-[#1A1A1A] flex items-center justify-between group transition-colors"
 data-testid={`brain-example-${i}`}>
 <span>{q}</span>
 <ChevronRight size={14} strokeWidth={1.5} className="text-[#6B7280] group-hover:text-[#1B6C62] group-hover:translate-x-0.5 transition-transform" />
 </button>
 ))}
 </div>
 </div>
 )}

 {/* Sample answers for signed-out visitors — static showpieces, zero AI cost */}
 {!isAuthed && (
 <div className="mt-8" data-testid="brain-samples">
 <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
 <div className="label-mono flex items-center gap-2">
 <Sparkles size={12} strokeWidth={1.5} className="text-[#1B6C62]" />
 <span>See what the Brain can solve</span>
 </div>
 <div className="flex gap-1 flex-wrap">
 {STATES.map(s => (
 <button key={s.code} onClick={() => setState(s.code)}
 className={`px-2 py-1 text-[11px] font-mono transition-colors ${state === s.code ? "bg-[#0D2B52] text-white" : "bg-white text-[#6B7280] border border-[#E5E4DE] hover:border-[#0D2B52]"}`}
 data-testid={`brain-sample-state-${s.code}`}>
 {s.code}
 </button>
 ))}
 </div>
 </div>
 <div className="space-y-3">
 {samples.map((q) => (
 <Link key={q.slug} to={`/q/${q.slug}`}
 className="block card card-hover" data-testid={`brain-sample-${q.slug}`}>
 <div className="flex items-center gap-2 mb-1.5">
 <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#1B6C62] bg-[#E6F5F2] border border-[#1B6C62]/30 px-1.5 py-0.5">{q.state}</span>
 <span className="font-mono text-[10px] text-[#6B7280]">Real question · full answer</span>
 </div>
 <h4 className="font-serif text-lg text-[#0D2B52] leading-snug mb-1">{q.title}</h4>
 <p className="text-[13px] text-[#6B7280] leading-relaxed mb-2">{q.teaser}</p>
 <span className="inline-flex items-center gap-1 text-[12px] font-mono uppercase tracking-[0.1em] text-[#1B6C62]">
 Read the full answer <ChevronRight size={12} strokeWidth={2} />
 </span>
 </Link>
 ))}
 </div>
 <div className="mt-4 text-center">
 <Link to="/q" className="text-sm text-[#1B6C62] hover:underline" data-testid="brain-samples-browse-all">
 Browse the full Renter Q&amp;A library →
 </Link>
 </div>
 </div>
 )}

 {/* NSW-only quick tool: Name & Shame check */}
 {state === "NSW" && (
 <div className="mt-6 card bg-[#FAF6E8] border-[#C9A961]/40" data-testid="brain-nsw-tool">
 <div className="flex items-start gap-3">
 <div className="flex-1">
 <div className="label-mono text-[#8A6D3B] mb-1">NSW · Quick tool</div>
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Check your agent&apos;s record</h3>
 <p className="text-[13px] text-[#1A1A1A] font-serif leading-relaxed mb-3">
 Search the NSW Fair Trading <strong>Name &amp; Shame Register</strong> for any disciplinary action,
 prosecution, or licence suspension against a real estate agent, strata manager, or stock &amp; station
 agent. Free, public. A smart first step before signing a lease.
 </p>
 <NswAgentCheck />
 </div>
 </div>
 </div>
 )}

 {/* Disclaimer + escape hatch */}
 <div className="mt-10">
 <div className="card bg-[#FAF9F6]">
 <div className="flex items-start gap-3">
 <BookOpen size={18} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5" />
 <div className="text-[13px] text-[#0D2B52] font-serif leading-relaxed">
 <strong>Guidance, not legal advice.</strong> The Brain draws on each state's Residential Tenancy Act, federal consumer + discrimination law and consistent tribunal practice. For advice on your specific situation, especially before serving a formal notice or filing at a tribunal, contact your state's <strong>Tenants' Union</strong> or community legal centre. Most offer <strong>free phone advice</strong>.
 </div>
 </div>
 </div>
 </div>

 <div className="mt-6">
 <Disclaimer state={state} />
 </div>
 </div>

 {/* Save modal */}
 {saveOpen && (
 <div className="fixed inset-0 z-50 bg-[#0D2B52]/70 flex items-center justify-center p-4" onClick={() => setSaveOpen(false)}>
 <div onClick={e => e.stopPropagation()} className="bg-white max-w-md w-full shadow-2xl" data-testid="brain-save-modal">
 <div className="px-5 py-3 border-b border-[#E5E4DE] flex items-center gap-2">
 <Save size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
 <h3 className="font-serif text-lg text-[#0D2B52]">Save to a new case</h3>
 </div>
 <div className="px-5 py-4 space-y-3">
 <p className="text-sm text-[#333] font-serif leading-relaxed">
 We'll create a new case for this property and drop the Q&A in as the first message. You can keep adding documents and ask follow-up questions with full case context.
 </p>
 <div>
 <label className="label-mono block mb-1.5">Property address (optional)</label>
 <input
 className="input-field"
 placeholder="42 Acacia Ave, Brisbane"
 value={saveAddress}
 onChange={e => setSaveAddress(e.target.value)}
 data-testid="brain-save-address"
 />
 </div>
 </div>
 <div className="px-5 py-3 border-t border-[#E5E4DE] flex justify-end gap-2">
 <button onClick={() => setSaveOpen(false)} className="btn-ghost text-sm">Cancel</button>
 <button onClick={saveToCase} disabled={saving} className="btn-primary text-sm" data-testid="brain-save-confirm">
 {saving ? "Saving…" : "Save and open"}
 </button>
 </div>
 </div>
 </div>
 )}
 
      <SiteFooter />
      </div>
 );
}

function ShareButton({ qaId }) {
 const [url, setUrl] = useState("");
 const [creating, setCreating] = useState(false);
 const [copied, setCopied] = useState(false);

 const share = async () => {
 setCreating(true);
 try {
 const { data } = await axios.post(`${API}/brain/share`, { qa_id: qaId }, {
 headers: localStorage.getItem("tt_token")
 ? { Authorization: `Bearer ${localStorage.getItem("tt_token")}` }
 : {},
 });
 const fullUrl = `${window.location.origin}${data.url}`;
 setUrl(fullUrl);
 // Try web share API first (mobile-friendly), fall back to clipboard.
 if (navigator.share) {
 try {
 await navigator.share({ url: fullUrl, title: "A tenancy answer from TenantTrail" });
 return;
 } catch (_) { /* user cancelled. keep going to clipboard */ }
 }
 try {
 await navigator.clipboard.writeText(fullUrl);
 setCopied(true);
 setTimeout(() => setCopied(false), 2000);
 } catch (_) { /* clipboard API unavailable in iframe / non-https, already shared via Web Share */ }
 } catch (e) {
 alert(e.response?.data?.detail || "Couldn't create share link");
 } finally { setCreating(false); }
 };

 return (
 <button onClick={share} disabled={creating}
 className="btn-ghost text-xs inline-flex items-center gap-1.5"
 data-testid={`brain-share-${qaId}`}>
 {copied ? <><Check size={12} strokeWidth={2} className="text-[#1B6C62]" /> Link copied!</>
 : <><Share2 size={12} strokeWidth={1.5} /> {url ? "Share again" : "Share this answer"}</>}
 </button>
 );
}
